import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { imageUrl } from '../lib/images'
import IndsatserModal from '../components/IndsatserModal'

export default function Home() {
  const [formData, setFormData] = useState({
    navn: '',
    telefonnummer: '',
    email: '',
    beskrivelse: '',
    website_url: '' // Honeypot
  })

  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null
  })

  const [selectedIndsats, setSelectedIndsats] = useState(null)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFormSubmit = async (e) => {
    e.preventDefault()
    setFormStatus({ submitting: true, success: false, error: null })

    if (!formData.navn.trim() || !formData.email.trim() || !formData.beskrivelse.trim()) {
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst de påkrævede felter (navn, email og besked).'
      })
      return
    }

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          navn: formData.navn,
          telefonnummer: formData.telefonnummer,
          email: formData.email,
          beskrivelse: formData.beskrivelse,
          website_url: formData.website_url
        })
      })

      if (response.ok) {
        setFormStatus({ submitting: false, success: true, error: null })
        setFormData({
          navn: '',
          telefonnummer: '',
          email: '',
          beskrivelse: '',
          website_url: ''
        })
      } else {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Der opstod en fejl. Prøv venligst igen.')
      }
    } catch (err) {
      setFormStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der opstod en fejl ved kontaktformularen. Ring gerne til 25 77 86 46.'
      })
    }
  }

  return (
    <div className="space-y-0">
      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-b from-[#0c304d] via-[#103b5f] to-[#0c304d] text-white pt-14 pb-20 sm:pt-20 sm:pb-28 overflow-hidden">
        <div className="absolute top-0 right-0 w-[550px] h-[550px] bg-[#f39223]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[450px] h-[450px] bg-sky-400/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs sm:text-sm font-medium text-amber-200 backdrop-blur-xs">
                <i className="fa-solid fa-heart text-[#f39223]" aria-hidden="true" />
                <span>Non-profit, upolitisk og ikke-religiøs forening</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15]">
                Velkommen til <br className="hidden sm:inline" />
                <span className="text-[#f39223]">Unges Fremtid</span>
              </h1>

              <p className="text-lg sm:text-xl text-neutral-200 max-w-2xl font-light leading-relaxed">
                Vi er startet af ildsjæle, der brænder for at støtte og hjælpe sårbare og udsatte børn og unge mellem 10 og 29 år til et godt liv.
              </p>

              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3 sm:gap-4">
                <a
                  href="#om-unges-fremtid"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 bg-[#f39223] hover:bg-[#e08112] text-white font-bold text-base px-7 py-3.5 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 cursor-pointer text-center"
                >
                  <span>Læs om foreningen</span>
                  <i className="fa-solid fa-arrow-down text-xs" aria-hidden="true" />
                </a>

                <a
                  href="tel:25778646"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold text-base px-6 py-3.5 rounded-full border border-white/25 transition-all duration-200 cursor-pointer text-center"
                >
                  <i className="fa-solid fa-phone text-[#f39223]" aria-hidden="true" />
                  <span className="whitespace-nowrap">Ring 25 77 86 46</span>
                </a>
              </div>

              <div className="pt-6 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[#f39223] shrink-0 text-sm">
                    <i className="fa-solid fa-shield-heart" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white">100% Non-profit</p>
                    <p className="text-[11px] text-neutral-300">Almennyttigt formål</p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[#f39223] shrink-0 text-sm">
                    <i className="fa-solid fa-user-check" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white">Mentorindsatser</p>
                    <p className="text-[11px] text-neutral-300">Central rolle</p>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-[#f39223] shrink-0 text-sm">
                    <i className="fa-solid fa-location-dot" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white">Hele Danmark</p>
                    <p className="text-[11px] text-neutral-300">Randers SØ base</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Billede i hero: Foto7 */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10 bg-neutral-900">
                  <img
                    src={imageUrl('/_assets/colourbox20897112.jpg', 'width=1000,quality=85,format=auto')}
                    alt="Samarbejde, fællesskab og relationer hos Unges Fremtid"
                    className="w-full h-[400px] sm:h-[480px] object-cover"
                    width={500}
                    height={480}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c304d]/90 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 p-4 rounded-2xl bg-white/95 backdrop-blur-md text-neutral-900 shadow-lg border border-neutral-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#f39223] text-white flex items-center justify-center font-bold text-lg shrink-0">
                        <i className="fa-solid fa-handshake-angle text-sm" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#0c304d]">Relationer der gør en forskel</p>
                        <p className="text-xs text-neutral-600">Børn og unge mellem 10 og 29 år</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION 1: OM UNGES FREMTID (Foto: COLOURBOX52763076) */}
      <section id="om-unges-fremtid" className="py-16 sm:py-24 bg-white border-b border-neutral-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Tekst */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#f39223]">
                <i className="fa-solid fa-info-circle" aria-hidden="true" />
                <span>Om foreningen</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0c304d] tracking-tight">
                Om Unges Fremtid
              </h2>

              <div className="space-y-4 text-neutral-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Unges Fremtid er en nonprofitorganisation, der arbejder for at hjælpe og styrke udsatte børn og unge til at få et meningsfyldt liv med stærke relationer i sunde fællesskaber, samt uddannelse og job.
                </p>
                <p>
                  Foreningens formål er at støtte og hjælpe sårbare og udsatte børn og unge mellem 10 og 29 år til et godt liv gennem udvikling af deres personlige, sociale, kulturelle, sproglige og faglige kompetencer.
                </p>
                <p>
                  Det sker gennem meningsfulde initiativer og målrettede projekter, hvor mentorindsatser ofte spiller en central rolle.
                </p>
              </div>

              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/om-unges-fremtid"
                  className="inline-flex items-center gap-2 bg-[#0c304d] hover:bg-[#16466e] text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-colors cursor-pointer shadow-xs"
                >
                  <span>Læs mere om Unges Fremtid</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <a
                  href="#kontakt"
                  className="inline-flex items-center gap-2 bg-amber-50 hover:bg-amber-100 text-[#0c304d] border border-amber-200 font-semibold text-sm px-6 py-3.5 rounded-full transition-colors cursor-pointer"
                >
                  <i className="fa-solid fa-envelope text-[#f39223]" aria-hidden="true" />
                  <span>Kontakt foreningen</span>
                </a>
              </div>
            </div>

            {/* Billede: COLOURBOX52763076 */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-neutral-100">
                  <img
                    src={imageUrl('/_assets/colourbox52763076.jpg', 'width=800,quality=85,format=auto')}
                    alt="Smilende ung pige der kigger over skulderen i bymiljø"
                    className="w-full h-[420px] sm:h-[480px] object-cover"
                    style={{ objectPosition: '75% 45%' }}
                    width={500}
                    height={480}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION 2: BØRN OG UNGE (Foto: COLOURBOX62002417) */}
      <section id="boern-og-unge" className="py-16 sm:py-24 bg-[#faf9f6] border-b border-neutral-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Billede: COLOURBOX62002417 */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-neutral-100">
                  <img
                    src={imageUrl('/_assets/colourbox62002417.jpg', 'width=800,quality=85,format=auto')}
                    alt="Ung mand med reflekteret blik"
                    className="w-full h-[420px] sm:h-[480px] object-cover"
                    style={{ objectPosition: '25% 35%' }}
                    width={500}
                    height={480}
                  />
                </div>
              </div>
            </div>

            {/* Tekst */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#f39223]">
                <i className="fa-solid fa-users" aria-hidden="true" />
                <span>Målgruppen</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0c304d] tracking-tight">
                Børn og unge
              </h2>

              <div className="space-y-4 text-neutral-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Børn og unge i dag rummer enorme ressourcer. De er nysgerrige, kreative, reflekterede og ofte langt mere åbne over for forskellighed end tidligere generationer. De engagerer sig i verden omkring dem, stiller spørgsmål og har både drømme og ambitioner for deres liv.
                </p>
                <p>
                  Men ikke alle får de samme muligheder for at føre drømmene ud i livet. Nogle børn og unge kæmper med ensomhed, mistrivsel, skolefravær, svære familieforhold, manglende tro på egne evner eller følelsen af ikke rigtigt at høre til. For nogle bliver vejen gennem skole og uddannelse og videre ind i voksenlivet derfor langt mere vanskelig end for andre.
                </p>
              </div>

              <div className="p-6 bg-white rounded-2xl border border-neutral-200/90 shadow-2xs space-y-2">
                <div className="flex items-center gap-2.5 text-[#0c304d] font-bold text-base">
                  <i className="fa-solid fa-circle-check text-[#f39223]" aria-hidden="true" />
                  <span>Vores fokus: Fra udfordring til trivsel og udvikling</span>
                </div>
                <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
                  Vi møder den enkelte unge i øjenhøjde og hjælper med at fjerne barriererne, så potentialet og drømmene kan folde sig ud.
                </p>
              </div>

              <div className="pt-2">
                <Link
                  to="/for-boern-og-unge"
                  className="inline-flex items-center gap-2 bg-[#0c304d] hover:bg-[#16466e] text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-colors cursor-pointer shadow-xs"
                >
                  <span>Læs mere til børn og unge</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION 3: HVORDAN GØR VI EN FORSKEL? (Foto: Foto7) */}
      <section id="forskellen" className="py-16 sm:py-24 bg-white border-b border-neutral-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Tekst */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#f39223]">
                <i className="fa-solid fa-hands-holding-heart" aria-hidden="true" />
                <span>Vores tilgang</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0c304d] tracking-tight">
                Hvordan gør vi en forskel?
              </h2>

              <div className="space-y-4 text-neutral-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Det positive er, at der ofte skal overraskende lidt til for at gøre en stor forskel. Et menneske, der ser dem. En voksen, der lytter. En mentor, der bliver ved med at tro på dem, også når de selv har mistet troen. En invitation ind i et fællesskab, en hjælpende hånd til den første praktikplads eller bare én, der spørger: Hvordan går det egentlig?
                </p>
                <p>
                  For et udsat barn eller en ung kan én stabil og positiv relation blive begyndelsen på noget helt nyt. Den kan skabe håb, styrke selvværdet og åbne døren til fællesskab, uddannelse og arbejde.
                </p>
                <p className="font-semibold text-[#0c304d]">
                  Nogle gange skal der ikke mere til end ét menneske, der vælger at gøre en forskel.
                </p>
                <p className="text-[#f39223] font-bold text-xl sm:text-2xl">
                  Og den forskel kan vare resten af livet.
                </p>
              </div>

              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href="tel:25778646"
                  className="inline-flex items-center gap-2 bg-[#f39223] hover:bg-[#e08112] text-white font-bold text-base px-7 py-3.5 rounded-full transition-colors cursor-pointer shadow-md"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Ring på 25 77 86 46</span>
                </a>

                <Link
                  to="/vores-arbejde"
                  className="inline-flex items-center gap-2 bg-neutral-100 hover:bg-neutral-200 text-[#0c304d] font-semibold text-base px-6 py-3.5 rounded-full transition-colors cursor-pointer"
                >
                  <span>Se vores indsatser</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Billede: Foto7 */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-neutral-100">
                  <img
                    src={imageUrl('/_assets/foto7.jpg', 'width=800,quality=85,format=auto')}
                    alt="To smilende kvinder bag disken i en café i et hjertevarmt samarbejde"
                    className="w-full h-[440px] sm:h-[500px] object-cover"
                    width={500}
                    height={500}
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-white/95 backdrop-blur-xs p-4 rounded-2xl border border-neutral-200 shadow-xl max-w-xs text-xs text-neutral-700">
                  <p className="font-bold text-[#0c304d] text-sm mb-1 flex items-center gap-2">
                    <i className="fa-solid fa-sparkles text-[#f39223]" aria-hidden="true" />
                    En stabil relation
                  </p>
                  <p>Begyndelsen på håb, styrket selvværd og åbne døre til fællesskab og arbejde.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION 4: INDSATSER (Foto: COLOURBOX20897112) */}
      <section id="indsatser" className="py-16 sm:py-24 bg-[#faf9f6] border-b border-neutral-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Billede: COLOURBOX20897112 */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-neutral-100">
                  <img
                    src={imageUrl('/_assets/colourbox20897112.jpg', 'width=800,quality=85,format=auto')}
                    alt="Ung person stående foran en skolebygning"
                    className="w-full h-[460px] sm:h-[540px] object-cover"
                    width={500}
                    height={540}
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-[#0c304d] text-white px-5 py-3 rounded-2xl shadow-lg font-bold text-sm hidden sm:flex items-center gap-2">
                  <i className="fa-solid fa-map-pin text-[#f39223]" aria-hidden="true" />
                  <span>Aktiviteter i hele Danmark</span>
                </div>
              </div>
            </div>

            {/* Tekst & Punkter */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#f39223]">
                <i className="fa-solid fa-list-check" aria-hidden="true" />
                <span>Vores kerneområder</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0c304d] tracking-tight">
                Indsatser
              </h2>

              <div className="space-y-4 text-neutral-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Foreningens formål er at støtte og hjælpe sårbare og udsatte unge mellem 10 og 29 år til et godt liv gennem udvikling af deres personlige, sociale, kulturelle, sproglige og faglige kompetencer.
                </p>
                <p className="font-semibold text-[#0c304d]">
                  Her kan du se eksempler på de områder, vi arbejder med lige nu:
                </p>
              </div>

              {/* De 8 punkter */}
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-2">
                {[
                  { id: 'trivsel', text: 'Styrke unges livsmestring og trivsel', icon: 'fa-heart-pulse' },
                  { id: 'forebygge', text: 'Forebygge ensomhed, marginalisering og kriminalitet', icon: 'fa-shield-halved' },
                  { id: 'uddannelse', text: 'Støtte unge i at gennemføre uddannelse', icon: 'fa-graduation-cap' },
                  { id: 'beskaeftigelse', text: 'Hjælpe unge i beskæftigelse eller anden meningsfuld aktivitet', icon: 'fa-briefcase' },
                  { id: 'mentor', text: 'Etablere mentorordninger, netværk og frivillige fællesskaber', icon: 'fa-user-group' },
                  { id: 'projekter', text: 'Udvikle og gennemføre sociale projekter og indsatser', icon: 'fa-diagram-project' },
                  { id: 'samarbejde', text: 'Skabe samarbejde mellem civilsamfund, kommuner, virksomheder og uddannelsesinstitutioner', icon: 'fa-handshake' },
                  { id: 'viden', text: 'Indsamle og formidle viden om virkningsfulde indsatser for unge', icon: 'fa-book-open-reader' }
                ].map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedIndsats(item)}
                      className="w-full h-full text-left p-3.5 rounded-xl bg-white border border-neutral-200/90 shadow-2xs hover:shadow-md hover:border-[#f39223]/70 hover:-translate-y-0.5 transition-all duration-200 flex items-start justify-between gap-3 cursor-pointer group focus:outline-none focus:ring-2 focus:ring-[#f39223]/40"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-50 text-[#f39223] group-hover:bg-[#f39223] group-hover:text-white transition-colors flex items-center justify-center shrink-0 mt-0.5 text-xs">
                          <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
                        </div>
                        <span className="text-sm font-medium text-neutral-800 group-hover:text-[#0c304d] leading-snug transition-colors">
                          {item.text}
                        </span>
                      </div>
                      <i className="fa-solid fa-arrow-right text-xs text-neutral-300 group-hover:text-[#f39223] group-hover:translate-x-0.5 transition-all mt-1 shrink-0" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>

              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200/80 flex items-center gap-3">
                <i className="fa-solid fa-location-dot text-[#f39223] text-lg shrink-0" aria-hidden="true" />
                <p className="text-sm font-bold text-[#0c304d]">
                  Foreningen udfører aktiviteter i hele Danmark.
                </p>
              </div>

              <div className="pt-2">
                <Link
                  to="/vores-arbejde"
                  className="inline-flex items-center gap-2 bg-[#0c304d] hover:bg-[#16466e] text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-colors cursor-pointer shadow-xs"
                >
                  <span>Læs mere om vores arbejde</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION 5: STØT OS (Foto: COLOURBOX63509247) */}
      <section id="stoet-os" className="py-16 sm:py-24 bg-white border-b border-neutral-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Tekst */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#f39223]">
                <i className="fa-solid fa-hand-holding-dollar" aria-hidden="true" />
                <span>Gør en forskel sammen med os</span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0c304d] tracking-tight">
                Støt os
              </h2>

              <div className="space-y-4 text-neutral-700 text-base sm:text-lg leading-relaxed">
                <p>
                  Når du støtter os – hvad enten det er gennem en donation, projektstøtte, et tilskud eller et sponsorat – skal du kunne være tryg ved, at pengene bliver brugt ansvarligt og med ét klart formål: at skabe bedre muligheder for børn og unge på vejen mod et godt, trygt og meningsfuldt liv.
                </p>
                <p>
                  Vi er en non-profit organisation. Det betyder, at alle midler bliver i organisationen og anvendes til vores almennyttige arbejde. Langt størstedelen går direkte til indsatser for børn og unge. Den del, der ikke går direkte til hjælpearbejdet, bruger vi på at sikre en professionel, effektiv og ansvarlig organisation, så vi kan skabe størst mulig værdi for de midler, vi modtager.
                </p>
                <p className="font-bold text-[#0c304d] text-lg sm:text-xl">
                  Din støtte er derfor en investering i børn og unges muligheder, trivsel og fremtid.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-[#faf9f6] border border-neutral-200 text-center">
                  <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">Gennemsigtighed</p>
                  <p className="text-base font-bold text-[#0c304d] mt-1">Ansvarlig drift</p>
                </div>
                <div className="p-4 rounded-2xl bg-[#faf9f6] border border-neutral-200 text-center">
                  <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">Almennyttigt</p>
                  <p className="text-base font-bold text-[#0c304d] mt-1">100% Non-profit</p>
                </div>
                <div className="p-4 rounded-2xl bg-[#faf9f6] border border-neutral-200 text-center">
                  <p className="text-xs text-neutral-500 uppercase tracking-wider font-semibold">Målrettet</p>
                  <p className="text-base font-bold text-[#0c304d] mt-1">Direkte til unge</p>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap gap-4">
                <a
                  href="#kontakt"
                  className="inline-flex items-center gap-2 bg-[#f39223] hover:bg-[#e08112] text-white font-bold text-base px-7 py-3.5 rounded-full transition-colors cursor-pointer shadow-md"
                >
                  <i className="fa-solid fa-heart" aria-hidden="true" />
                  <span>Kontakt os om støtte og sponsorater</span>
                </a>

                <Link
                  to="/bliv-involveret"
                  className="inline-flex items-center gap-2 bg-neutral-100 hover:bg-neutral-200 text-[#0c304d] font-semibold text-base px-6 py-3.5 rounded-full transition-colors cursor-pointer"
                >
                  <span>Bliv involveret</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Billede: COLOURBOX63509247 */}
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-xl border-4 border-white bg-neutral-100">
                  <img
                    src={imageUrl('/_assets/colourbox63509247.jpg', 'width=800,quality=85,format=auto')}
                    alt="Ung smilende kvinde med lyst hår og vinterjakke udendørs"
                    className="w-full h-[440px] sm:h-[500px] object-cover"
                    width={500}
                    height={500}
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-[#0c304d] text-white px-5 py-3 rounded-2xl shadow-lg font-bold text-sm hidden sm:flex items-center gap-2">
                  <i className="fa-solid fa-circle-check text-[#f39223]" aria-hidden="true" />
                  <span>En investering i fremtiden</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEKTION 6: NYHEDER (Foto: Logo) */}
      <section id="nyheder" className="py-16 sm:py-24 bg-[#faf9f6] border-b border-neutral-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12 space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#f39223]">
              Aktuelt
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0c304d] tracking-tight">
              Nyheder
            </h2>
          </div>

          <article className="bg-white rounded-3xl p-6 sm:p-12 border border-neutral-200 shadow-md">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-8 space-y-5">
                <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#f39223] bg-amber-50 px-3 py-1 rounded-full border border-amber-200/80">
                  <i className="fa-solid fa-calendar-day" aria-hidden="true" />
                  <span>Stiftet den 7. august 2026</span>
                </div>

                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#0c304d] leading-snug">
                  Ny forening vil hjælpe udsatte unge på vej mod det gode liv
                </h3>

                <p className="text-base sm:text-lg text-neutral-700 leading-relaxed font-medium">
                  Den nystiftede landsdækkende forening Unges Fremtid vil skabe nye muligheder for sårbare og udsatte unge mellem 10 og 29 år. Gennem blandt andet mentorindsatser, frivillige fællesskaber og samarbejde med kommuner, virksomheder og uddannelsesinstitutioner skal flere unge hjælpes til trivsel, uddannelse og beskæftigelse.
                </p>

                <div className="pt-2">
                  <Link
                    to="/nyheder"
                    className="inline-flex items-center gap-2.5 bg-[#f39223] hover:bg-[#e08112] text-white font-semibold text-sm sm:text-base px-6 py-3 rounded-full transition-all duration-200 shadow-xs hover:shadow-md cursor-pointer group"
                  >
                    <span>Læs hele artiklen</span>
                    <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                  </Link>
                </div>
              </div>

              {/* Logo Foto */}
              <div className="lg:col-span-4 flex justify-center">
                <div className="p-6 sm:p-8 bg-white rounded-2xl shadow-md border border-neutral-200/80 max-w-[340px] w-full flex items-center justify-center">
                  <img
                    src={imageUrl('/_assets/unges-fremtid-logo.png', 'width=600,quality=95,format=auto')}
                    alt="Unges Fremtid logo"
                    className="w-full h-auto object-contain max-h-36 sm:max-h-40"
                    width={320}
                    height={110}
                  />
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* SEKTION 7: KONTAKT */}
      <section id="kontakt" className="py-16 sm:py-24 bg-white scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12 space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-[#f39223]">
              Kom i kontakt
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0c304d] tracking-tight">
              Kontakt
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Foreningsoplysninger */}
            <div className="lg:col-span-5 space-y-8">
              <div className="bg-[#faf9f6] rounded-3xl p-6 sm:p-8 border border-neutral-200/90 shadow-2xs space-y-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-[#f39223]">
                    Foreningsoplysninger
                  </span>
                  <h3 className="text-2xl font-extrabold text-[#0c304d] mt-1">
                    Unges Fremtid
                  </h3>
                </div>

                <div className="space-y-4 text-sm text-neutral-700">
                  {/* Adresse */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-location-dot" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">Adresse</p>
                      <p className="text-neutral-700 text-base">Koglevej 36, 8960 Randers SØ</p>
                    </div>
                  </div>

                  {/* Telefon (klik for opkald) */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-phone" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">Telefon (klik for opkald)</p>
                      <a
                        href="tel:25778646"
                        className="text-[#0c304d] hover:text-[#f39223] font-extrabold text-lg transition-colors whitespace-nowrap block"
                      >
                        25 77 86 46
                      </a>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Ring på hverdage mellem kl. 9.00 og 15.00
                      </p>
                    </div>
                  </div>

                  {/* E-mail (klik for mail) */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-envelope" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">E-mail (klik for mail)</p>
                      <a
                        href="mailto:ungesfremtid@outlook.dk"
                        className="text-[#0c304d] hover:text-[#f39223] font-bold text-base transition-colors break-all block"
                      >
                        ungesfremtid@outlook.dk
                      </a>
                    </div>
                  </div>

                  {/* CVR-nr */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-id-card" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">CVR-nr:</p>
                      <p className="text-neutral-800 font-semibold">46681606</p>
                    </div>
                  </div>

                  {/* Bankoplysninger */}
                  <div className="flex items-start gap-3.5">
                    <div className="w-10 h-10 rounded-xl bg-white border border-neutral-200 flex items-center justify-center text-[#f39223] shrink-0 shadow-2xs">
                      <i className="fa-solid fa-building-columns" aria-hidden="true" />
                    </div>
                    <div>
                      <p className="font-bold text-neutral-900">Bankoplysninger</p>
                      <p className="text-neutral-800 font-semibold text-base">Reg. nr. 6124</p>
                      <p className="text-neutral-800 font-semibold text-base">Konto 0024431401</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-neutral-200 text-xs text-neutral-600 space-y-1">
                  <p className="font-semibold text-neutral-800">
                    Telefontid: Hverdage kl. 9.00 – 15.00
                  </p>
                  <p>
                    Du er også altid velkommen til at sende os en mail på{' '}
                    <a href="mailto:ungesfremtid@outlook.dk" className="underline hover:text-[#f39223]">
                      ungesfremtid@outlook.dk
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Kontaktformular: Send os en besked */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-3xl p-6 sm:p-10 border border-neutral-200 shadow-md">
                <h3 className="text-2xl font-bold text-[#0c304d] mb-2">
                  Send os en besked
                </h3>
                <p className="text-sm sm:text-base text-neutral-600 mb-6 leading-relaxed">
                  Udfyld formularen herunder med din henvendelse, så vender vi tilbage hurtigst muligt.
                </p>
                <p className="text-xs sm:text-sm text-neutral-500 mb-6">
                  Du kan også ringe til vores hovednummer på <a href="tel:25778646" className="font-bold text-[#0c304d] underline">25 77 86 46</a> på hverdage mellem kl. 9.00 og 15.00.
                </p>

                {formStatus.success ? (
                  <div className="p-8 rounded-2xl bg-green-50 border border-green-200 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-3xl mx-auto">
                      <i className="fa-solid fa-check" aria-hidden="true" />
                    </div>
                    <h4 className="text-xl font-bold text-green-900">
                      Tak for din henvendelse!
                    </h4>
                    <p className="text-neutral-700 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
                      Vi har modtaget din besked og vender hurtigt tilbage. Hvis det haster, kan du ringe direkte til os på <strong className="text-neutral-900">25 77 86 46</strong>.
                    </p>
                    <button
                      type="button"
                      onClick={() => setFormStatus({ submitting: false, success: false, error: null })}
                      className="mt-4 px-6 py-2.5 bg-[#0c304d] hover:bg-[#16466e] text-white rounded-full text-sm font-semibold transition-colors cursor-pointer"
                    >
                      Send endnu en besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleFormSubmit} className="space-y-5" noValidate>
                    {/* ANTISPAM HONEYPOT */}
                    <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                      <label htmlFor="website_url">Website URL (leave empty)</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleInputChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div>
                      <label htmlFor="home-navn" className="block text-sm font-bold text-neutral-800 mb-1.5">
                        Dit fulde navn <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="home-navn"
                        name="navn"
                        value={formData.navn}
                        onChange={handleInputChange}
                        placeholder="F.eks. Mette Hansen"
                        required
                        className="w-full px-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#f39223] focus:border-transparent text-sm bg-neutral-50/50"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="home-telefonnummer" className="block text-sm font-bold text-neutral-800 mb-1.5">
                          Telefonnummer
                        </label>
                        <input
                          type="tel"
                          id="home-telefonnummer"
                          name="telefonnummer"
                          value={formData.telefonnummer}
                          onChange={handleInputChange}
                          placeholder="F.eks. 12 34 56 78"
                          className="w-full px-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#f39223] focus:border-transparent text-sm bg-neutral-50/50"
                        />
                      </div>

                      <div>
                        <label htmlFor="home-email" className="block text-sm font-bold text-neutral-800 mb-1.5">
                          E-mailadresse <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="home-email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="din@email.dk"
                          required
                          className="w-full px-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#f39223] focus:border-transparent text-sm bg-neutral-50/50"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="home-beskrivelse" className="block text-sm font-bold text-neutral-800 mb-1.5">
                        Din henvendelse / besked <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="home-beskrivelse"
                        name="beskrivelse"
                        rows={5}
                        value={formData.beskrivelse}
                        onChange={handleInputChange}
                        placeholder="Skriv hvad du henvender dig om..."
                        required
                        className="w-full px-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-[#f39223] focus:border-transparent text-sm bg-neutral-50/50 resize-y"
                      />
                    </div>

                    {formStatus.error && (
                      <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
                        <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
                        {formStatus.error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus.submitting}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#f39223] hover:bg-[#e08112] text-white font-bold text-base px-8 py-3.5 rounded-full transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 cursor-pointer text-center"
                    >
                      {formStatus.submitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                          <span>Sender besked...</span>
                        </>
                      ) : (
                        <>
                          <span>Send besked</span>
                          <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* MODAL FOR INDSATSER */}
      <IndsatserModal
        indsats={selectedIndsats}
        onClose={() => setSelectedIndsats(null)}
      />
    </div>
  )
}
